import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  CardHeader,
  Chip
} from '@mui/material'
import {
  Send,
  Cancel,
  Search,
  Person,
  Business
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format, addDays } from 'date-fns'
import fileMovementService from '../../../services/fileMovementService'
import mrdService from '../../../services/mrdservice'

const MovementForm = ({ fileId, onSuccess, onCancel }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [mrdFile, setMrdFile] = useState(null)
  const [formData, setFormData] = useState({
    mrdFile: fileId || '',
    movementType: 'DEPARTMENT_TRANSFER',
    fromLocation: '',
    toLocation: '',
    issuedTo: '',
    purpose: '',
    expectedReturnDate: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
    priority: 'NORMAL',
    remarks: ''
  })
  
  const [departments, setDepartments] = useState([])
  const [users, setUsers] = useState([])
  const [mrdFiles, setMrdFiles] = useState([])
  const [fileSearchTerm, setFileSearchTerm] = useState('')

  useEffect(() => {
    loadInitialData()
  }, [fileId])

  const loadInitialData = async () => {
    setLoading(true)
    try {
      const [deptResponse, usersResponse, filesResponse] = await Promise.all([
        mrdService.getDepartments(),
        mrdService.getUsers({ role: 'ALL' }),
        mrdService.getMRDFiles({ limit: 100, status: 'ACTIVE' })
      ])

      if (deptResponse.data.success) {
        setDepartments(deptResponse.data.data)
      }

      if (usersResponse.data.success) {
        setUsers(usersResponse.data.data)
      }

      if (filesResponse.data.success) {
        setMrdFiles(filesResponse.data.data)
      }

      if (fileId) {
        const fileResponse = await mrdService.getFileById(fileId)
        if (fileResponse.data.success) {
          setMrdFile(fileResponse.data.data)
          setFormData(prev => ({
            ...prev,
            fromLocation: fileResponse.data.data.currentLocation || 'MRD Department'
          }))
        }
      }
    } catch (error) {
      enqueueSnackbar('Failed to load form data', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.mrdFile) {
      enqueueSnackbar('Please select an MRD file', { variant: 'error' })
      return
    }
    
    if (!formData.toLocation) {
      enqueueSnackbar('Please select destination location', { variant: 'error' })
      return
    }
    
    if (!formData.issuedTo) {
      enqueueSnackbar('Please enter who the file is issued to', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const response = await fileMovementService.createMovement(formData)
      
      if (response.data.success) {
        enqueueSnackbar('File movement created successfully', { variant: 'success' })
        if (onSuccess) onSuccess(response.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to create file movement', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const movementTypes = [
    { value: 'DEPARTMENT_TRANSFER', label: 'Department Transfer' },
    { value: 'EXTERNAL_REQUEST', label: 'External Request' },
    { value: 'AUDIT_REVIEW', label: 'Audit Review' },
    { value: 'LEGAL_REVIEW', label: 'Legal Review' },
    { value: 'INSURANCE_CLAIM', label: 'Insurance Claim' },
    { value: 'RESEARCH', label: 'Research' },
    { value: 'OTHER', label: 'Other' }
  ]

  const priorities = [
    { value: 'LOW', label: 'Low', color: 'default' },
    { value: 'NORMAL', label: 'Normal', color: 'primary' },
    { value: 'HIGH', label: 'High', color: 'warning' },
    { value: 'URGENT', label: 'Urgent', color: 'error' }
  ]

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={3}>
        {/* File Selection */}
        {!fileId && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="Select MRD File" />
              <CardContent>
                <Autocomplete
                  options={mrdFiles}
                  getOptionLabel={(option) => `${option.fileNumber} - ${option.patientName}`}
                  value={mrdFiles.find(f => f._id === formData.mrdFile) || null}
                  onChange={(e, value) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      mrdFile: value?._id || '',
                      fromLocation: value?.currentLocation || 'MRD Department'
                    }))
                    setMrdFile(value)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search MRD File *"
                      placeholder="Search by file number or patient name..."
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.fileNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.patientName} • {option.currentLocation || 'MRD Department'}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* File Information */}
        {mrdFile && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title="File Information" />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      File Number
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {mrdFile.fileNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Patient Name
                    </Typography>
                    <Typography variant="body1">
                      {mrdFile.patientName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current Location
                    </Typography>
                    <Chip
                      label={mrdFile.currentLocation || 'MRD Department'}
                      color="primary"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={mrdFile.status}
                      color={mrdFile.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Movement Details */}
        <Grid item xs={12}>
          <Card>
            <CardHeader title="Movement Details" />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Movement Type</InputLabel>
                    <Select
                      value={formData.movementType}
                      label="Movement Type"
                      onChange={(e) => setFormData(prev => ({ ...prev, movementType: e.target.value }))}
                    >
                      {movementTypes.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={formData.priority}
                      label="Priority"
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                    >
                      {priorities.map(priority => (
                        <MenuItem key={priority.value} value={priority.value}>
                          <Chip
                            label={priority.label}
                            color={priority.color}
                            size="small"
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="From Location"
                    value={formData.fromLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, fromLocation: e.target.value }))}
                    InputProps={{
                      startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    disabled={!!mrdFile?.currentLocation}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="To Location *"
                    value={formData.toLocation}
                    onChange={(e) => setFormData(prev => ({ ...prev, toLocation: e.target.value }))}
                    InputProps={{
                      startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    placeholder="Enter destination location"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Issued To *"
                    value={formData.issuedTo}
                    onChange={(e) => setFormData(prev => ({ ...prev, issuedTo: e.target.value }))}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    placeholder="Enter person/department name"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Expected Return Date"
                    value={formData.expectedReturnDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, expectedReturnDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    value={formData.remarks}
                    onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Additional remarks or instructions..."
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          startIcon={<Send />}
        >
          Create Movement
        </Button>
      </Box>
    </Box>
  )
}

export default MovementForm